import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { type SortingState, useTable } from '@tanstack/react-table';
import { CheckCircle2, Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext } from 'react-router';
import { toast } from 'sonner';

import type { DamageQueryParams } from '@/common/damage';
import { ROLE } from '@/common/role';
import { BulkActionBar } from '@/components/bulk/BulkActionBar';
import { DataTable } from '@/components/data-table/data-table';
import { toSortParam } from '@/components/data-table/sort-param';
import { DamageIcon } from '@/components/entity-icons';
import { Restricted } from '@/components/Restricted';
import { DeletedViewBanner } from '@/components/soft-delete/DeletedViewBanner';
import { RestoreConfirmDialog } from '@/components/soft-delete/RestoreConfirmDialog';
import { Button } from '@/components/ui/button';
import { useMobileAssetTab } from '@/context/MobileAssetTabContext';
import { useBulkSelection } from '@/hooks/use-bulk-selection';
import { deletedPreviewHref } from '@/hooks/use-deleted-preview';
import { DROPDOWN_NAMES, getDropdownOptionByDropdown } from '@/hooks/use-dropdowns';
import { usePermission } from '@/hooks/use-permission';
import { useRestoreItem } from '@/hooks/use-soft-delete';
import { reportBulkDelete, reportBulkRestore, runBulkDelete, runBulkRestore } from '@/lib/utils/bulk-delete';
import { invalidateAfterSoftDelete } from '@/lib/utils/soft-delete';
import { getListDamagesByInspectionOptions } from '@/queries/damages/list-damages';
import { queryClient } from '@/queries/query.client';
import {
	getBulkDamageDeletePreview,
	restoreItem,
	softDeleteItem,
	softDeleteTargets,
} from '@/queries/soft-delete/soft-delete';
import { getListSubAssetsOptions } from '@/queries/sub-assets/list-sub-assets';
import { BulkDamageDeleteSummary } from './BulkDamageDeleteSummary';
import { planBulkDamageDelete } from './bulk-damage-delete';
import {
	DAMAGE_SORT_FIELDS,
	type DamageListItem,
	type DamageTableMeta,
	damageColumns,
	damageTableFeatures,
	toDamageListItem,
} from './columns';
import { ALL_FILTER_VALUE, DamageFilters, type DamageFilterValues } from './damage-filters';

// Server-side page size; there is no pager UI yet, so this is the max number of damages shown.
const PAGE_SIZE = 100;

export type DamageListProps = {
	assetId: string;
	inspectionId: string;
};

const toFilterParam = (value: string) => (value !== ALL_FILTER_VALUE ? value : undefined);

export function DamageList({ assetId, inspectionId }: DamageListProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { mapActions } = useOutletContext<{
		mapActions: (action: { type: string; payload: { damageId: string } }) => void;
	}>();
	const shouldReadDamages = usePermission(ROLE.DAMAGES_READ);
	const canDeleteDamages = usePermission(ROLE.DAMAGES_DELETE);
	const { setActiveTab, showMapTab } = useMobileAssetTab();

	// Status options drive both the default status filter and the filter dropdown. The damages
	// query waits for them so the first request already carries the default status filter.
	const { isLoading: isOptionsLoading, options } = getDropdownOptionByDropdown(DROPDOWN_NAMES.DAMAGE_STATUSES);
	const getDefaultStatusIds = () =>
		options?.filter?.((opt) => opt.value !== 'INSTANDGESETZT').map((opt) => opt.id.toString()) ?? [];

	// -- State --
	const [sorting, setSorting] = useState<SortingState>([]);
	// Status "Deleted" (GH #331): only this inspection's deleted damages, per page visit; it replaces the status filter
	const [showDeleted, setShowDeletedState] = useState(false);
	const deleted = canDeleteDamages && showDeleted;
	const [damageToRestore, setDamageToRestore] = useState<DamageListItem | null>(null);
	const restoreDamage = useRestoreItem({ onRestored: () => setDamageToRestore(null) });
	const [filters, setFilters] = useState<DamageFilterValues>(() => {
		const defaultStatusIds = getDefaultStatusIds();
		return {
			damageClass: ALL_FILTER_VALUE,
			materialCategory: ALL_FILTER_VALUE,
			subAssetId: ALL_FILTER_VALUE,
			statusIds: defaultStatusIds.length ? defaultStatusIds : null,
		};
	});

	const handleFilterChange = <K extends keyof DamageFilterValues>(key: K, value: DamageFilterValues[K]) =>
		setFilters((prev) => ({ ...prev, [key]: value }));

	// Options load asynchronously; apply the default statuses once they arrive.
	useEffect(() => {
		if (filters.statusIds === null && options.length > 0) {
			const defaultStatusIds = options.filter((opt) => opt.value !== 'INSTANDGESETZT').map((opt) => opt.id.toString());
			if (defaultStatusIds.length > 0) {
				setFilters((prev) => ({ ...prev, statusIds: defaultStatusIds }));
			}
		}
	}, [options, filters.statusIds]);

	// -- Query Params --
	const queryParams = useMemo<DamageQueryParams>(() => {
		const { statusIds } = filters;
		// Selecting every status is the same as no status filter, so skip it.
		const shouldIncludeStatusFilter =
			statusIds !== null && statusIds.length > 0 && options.length > 0 && statusIds.length !== options.length;
		return {
			limit: PAGE_SIZE,
			offset: 0,
			class: toFilterParam(filters.damageClass),
			materialCategory: toFilterParam(filters.materialCategory),
			subAssetId: toFilterParam(filters.subAssetId),
			statusIds: shouldIncludeStatusFilter && !deleted ? statusIds.join(',') : undefined,
			sort: toSortParam(sorting, DAMAGE_SORT_FIELDS),
			...(deleted && { deleted: true }),
		};
	}, [filters, sorting, options.length, deleted]);

	// -- Data Fetching --
	const { data, isLoading, isPlaceholderData } = useQuery({
		...getListDamagesByInspectionOptions(
			inspectionId,
			queryParams,
			!!assetId && shouldReadDamages && !isOptionsLoading,
		),
		// Keep the current rows on screen while a re-sort or re-filter loads instead of flashing the spinner.
		placeholderData: keepPreviousData,
	});
	const { data: subAssetsResponse } = useQuery(getListSubAssetsOptions(assetId || '', { limit: 300, offset: 0 }));
	const subAssets = subAssetsResponse?.subAssets ?? [];

	const tableData = useMemo(() => (data?.damages ?? []).map(toDamageListItem), [data?.damages]);

	// -- Multi-select + bulk delete --
	const damageIds = useMemo(() => tableData.map((damage) => damage.id), [tableData]);
	const selection = useBulkSelection(damageIds);
	const [isBulkDeleting, setIsBulkDeleting] = useState(false);
	const itemLabel = {
		singular: t('common.entities.damage', { count: 1 }),
		plural: t('common.entities.damage', { count: 2 }),
	};
	// The rows change completely: a selection of active damages must not reach "Restore selected"
	const setShowDeleted = (next: boolean) => {
		selection.clear();
		setShowDeletedState(next);
	};
	const restoreDamages = async (ids: string[]) => {
		const result = await runBulkRestore(ids, (id) => restoreItem(softDeleteTargets.damage(id)));
		reportBulkRestore(result, itemLabel);
		await invalidateAfterSoftDelete(queryClient, result.deleted);
	};
	// The bulk confirmation checks the selection first (GH #331): damages recorded in a work package
	// are left out and listed with links to those packages; the others go ahead
	const [isBulkConfirmOpen, setIsBulkConfirmOpen] = useState(false);
	const bulkPreview = useQuery({
		queryKey: ['soft-delete', 'bulk-delete-preview', 'damages', selection.selectedIds],
		queryFn: () => getBulkDamageDeletePreview(selection.selectedIds),
		enabled: isBulkConfirmOpen && !deleted && selection.count > 0,
		gcTime: 0,
		staleTime: 0,
		meta: { skipGlobalErrorToast: true },
	});
	const bulkPlan = useMemo(
		() => planBulkDamageDelete(selection.selectedIds, bulkPreview.data ?? undefined),
		[selection.selectedIds, bulkPreview.data],
	);
	const damageTitle = (id: string) =>
		tableData.find((damage) => damage.id === id)?.title || t('common.entities.damage', { count: 1 });
	const handleBulkDelete = async () => {
		setIsBulkDeleting(true);
		try {
			// Only what the preview cleared; damages recorded in a work package are left out
			const result = await runBulkDelete(bulkPlan.deletableIds, (id) => softDeleteItem(softDeleteTargets.damage(id)));
			reportBulkDelete(result, itemLabel, (deletedIds) => void restoreDamages(deletedIds));
			selection.clear();
			// The list and the polygons drawn on the map both come from the deleted rows
			await invalidateAfterSoftDelete(queryClient, result.deleted);
		} finally {
			setIsBulkDeleting(false);
		}
	};
	const handleBulkRestore = async () => {
		setIsBulkDeleting(true);
		try {
			await restoreDamages(selection.selectedIds);
			selection.clear();
		} finally {
			setIsBulkDeleting(false);
		}
	};

	// -- Table Meta Handlers --
	const damageUrl = (damageId: string) => `/assets/${assetId}/inspections/${inspectionId}/damages/${damageId}`;

	const handleHighlightDamage: DamageTableMeta['handleHighlightDamage'] = (damageId, hasPolygons, e) => {
		e.stopPropagation();
		if (!hasPolygons) return;
		if (showMapTab) setActiveTab('map');
		mapActions({ type: 'zoom', payload: { damageId } });
	};

	const handleCopyDamageLink: DamageTableMeta['handleCopyDamageLink'] = async (damageId, e) => {
		e.stopPropagation();
		try {
			await navigator.clipboard.writeText(`${window.location.origin}${damageUrl(damageId)}`);
			toast.success(t('damages.toast.linkCopiedToClipboard'));
		} catch {
			toast.error(t('damages.toast.linkCopyFailed'));
		}
	};

	// -- Table Instance --
	const table = useTable({
		features: damageTableFeatures,
		columns: damageColumns,
		data: tableData,
		state: { sorting, columnVisibility: { select: canDeleteDamages } },
		onSortingChange: setSorting,
		// The API sorts the whole result set; sorting client-side would only reorder the fetched page.
		manualSorting: true,
		// Skip auto-detecting the first direction from row values; always start ascending.
		sortDescFirst: false,
		meta: {
			handleHighlightDamage,
			handleCopyDamageLink,
			selection,
			handleRestore: deleted ? setDamageToRestore : undefined,
		},
	});

	return (
		<div className="space-y-4">
			<div className="flex flex-col gap-4 mb-4">
				<div className="flex justify-between items-center">
					<h3 className="text-foreground/80 font-medium text-lg flex items-center gap-2">
						<DamageIcon
							className="size-5 shrink-0 text-muted-foreground"
							aria-label={t('common.entities.damage', { count: 1 })}
						/>
						{t('damages.list.title')}
					</h3>
					<Restricted to={ROLE.DAMAGES_CREATE}>
						<Button size="sm" onClick={() => navigate(damageUrl('new'))} className="shadow-sm">
							<Plus className="h-4 w-4 mr-1" /> {t('damages.list.new')}
						</Button>
					</Restricted>
				</div>

				<DamageFilters
					values={filters}
					onChange={handleFilterChange}
					subAssets={subAssets}
					deletedOption={canDeleteDamages ? { selected: showDeleted, onSelectedChange: setShowDeleted } : undefined}
				/>
				{deleted && <DeletedViewBanner />}
			</div>

			{canDeleteDamages && selection.count > 0 && (
				<BulkActionBar
					count={selection.count}
					total={tableData.length}
					label={itemLabel}
					mode={deleted ? 'restore' : 'delete'}
					isDeleting={isBulkDeleting}
					onClear={selection.clear}
					onDelete={deleted ? handleBulkRestore : handleBulkDelete}
					onConfirmOpenChange={setIsBulkConfirmOpen}
					confirmBody={
						!deleted && (
							<BulkDamageDeleteSummary
								plan={bulkPlan}
								isLoading={bulkPreview.isLoading}
								isError={bulkPreview.isError}
								titleOf={damageTitle}
							/>
						)
					}
					confirmLabel={
						!deleted && bulkPlan.deletableIds.length < selection.count
							? t('common.bulk.deleteSome', { count: bulkPlan.deletableIds.length, total: selection.count })
							: undefined
					}
					confirmDisabled={!deleted && (bulkPreview.isLoading || bulkPlan.deletableIds.length === 0)}
				/>
			)}

			<DataTable
				table={table}
				isLoading={isLoading}
				isFetching={isPlaceholderData}
				tableClassName="table-fixed w-full"
				onRowClick={(row) =>
					navigate(deleted ? deletedPreviewHref(damageUrl(row.original.id), 'damage') : damageUrl(row.original.id))
				}
				getRowClassName={(row) => selection.isSelected(row.original.id) && 'bg-primary/5'}
				emptyState={
					<>
						<CheckCircle2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
						<p>{deleted ? t('common.deleted.empty') : t('damages.list.empty')}</p>
					</>
				}
			/>
			{damageToRestore && (
				<RestoreConfirmDialog
					open
					onOpenChange={(open) => !open && setDamageToRestore(null)}
					target={softDeleteTargets.damage(damageToRestore.id)}
					name={damageToRestore.title || t('common.entities.damage', { count: 1 })}
					isRestoring={restoreDamage.isPending}
					onConfirm={() =>
						restoreDamage.mutate({
							target: softDeleteTargets.damage(damageToRestore.id),
							name: damageToRestore.title || t('common.entities.damage', { count: 1 }),
						})
					}
				/>
			)}
		</div>
	);
}
